/**
 * The authentication boundary, on the console side.
 *
 * Two paths, chosen by what the service reports at /auth/sso/config. When a
 * client id is configured, MSAL owns the login. When it is not, the console
 * runs on the development bypass, and it says so on every page rather than
 * looking authenticated.
 *
 * The console never decides what a role may do. It reads the principal to
 * choose what to render, and every endpoint checks the role again on the
 * service side. Rendering is not authorisation (ADR-006).
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError, api, useDevUser } from './api'
import { MSAL_REDIRECT_PATH, landingAfterSignIn } from './signInLanding'
import { clearStuckSignInState, describeSignInFailure } from './signInRecovery'
import type { Principal } from './api'

interface SsoConfig {
  tenant_id: string
  client_id: string
}

interface AuthState {
  principal: Principal | null
  loading: boolean
  error: string | null
  usingDevBypass: boolean
  /** Absent until Lucian creates the app registration. The sign-in screen
   *  shows the Microsoft button disabled while it is, and says why. */
  tenantRegistered: boolean
  signIn: () => void
  /** The transition path, removed at the KW35 cleanup (ADR-006, DEC-06).
   *  Resolves to an error message, or null when the session was established. */
  signInWithPassword: (email: string, password: string) => Promise<string | null>
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

const DEV_USER_KEY = 'walle.eas.devUser'

/**
 * Inside the router, deliberately, and it will throw outside one.
 *
 * This provider is the only thing that knows when the sign-in response has
 * been consumed, and that is the moment the person has to be taken off the
 * delivery address (signInLanding). Inferring it one layer out from `loading`
 * would be a proxy for the fact rather than the fact.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  // The address the browser arrived on, fixed at mount. Read from the router
  // and not from `window.location`, because they are two different sources
  // and only one of them is what the app then navigates within.
  const landedAt = useRef(useLocation().pathname)
  const [principal, setPrincipal] = useState<Principal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sso, setSso] = useState<SsoConfig | null>(null)

  useEffect(() => {
    let cancelled = false

    async function boot() {
      try {
        const config = await api.get<SsoConfig>('/auth/sso/config')
        if (cancelled) return
        setSso(config)

        if (!config.client_id) {
          // No app registration yet. The bypass needs a name to send, and the
          // service still decides whether that name may do anything.
          const remembered = localStorage.getItem(DEV_USER_KEY)
          if (remembered) useDevUser(remembered)
        } else {
          // Allowed to fail, and nothing after it depends on it succeeding.
          // Not being signed in is the ordinary case, and so is a cached
          // account whose token has expired. Awaited unguarded, either of
          // those threw past this line and the console rendered "the service
          // is not answering" over a service that was answering, with no
          // sign-in screen behind it and no way back except clearing the
          // browser's app data.
          await quietSignIn(config)
        }

        const me = await api.get<Principal>('/auth/me')
        if (!cancelled) setPrincipal(me)
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.isUnauthenticated) setPrincipal(null)
        // The detail when it says more than the title. Every 403 carries the
        // title "Your role does not permit this action", because the title is
        // per status; an account with a perfect tenant registration and no row
        // in operator_principals is refused with that sentence and the person
        // is sent to check a role that was never the problem. It cost an hour.
        else if (err instanceof ApiError) setError(problemText(err))
        else setError('The service is not answering')
      } finally {
        if (!cancelled) {
          setLoading(false)
          // Here and not earlier: by this line the response has been read,
          // whatever it said. Navigating sooner would take the URL away from
          // `handleRedirectPromise` before it had looked at it, which is the
          // defect this is next door to rather than a repair of it.
          //
          // `replace` so the delivery address is not left in the history. The
          // back button would otherwise return to it with nothing left to
          // deliver, which is the same 404 one press away.
          const landing = landingAfterSignIn(landedAt.current)
          if (landing) navigate(landing, { replace: true })
        }
      }
    }

    void boot()
    return () => {
      cancelled = true
    }
  }, [])

  const state: AuthState = {
    principal,
    loading,
    error,
    usingDevBypass: principal?.via_dev_bypass ?? false,
    tenantRegistered: Boolean(sso?.client_id),
    signIn: () => {
      if (sso?.client_id) void signInWithMsal(sso, { silent: false })
      else {
        const email = window.prompt('Development bypass. Which account?')
        if (email) {
          localStorage.setItem(DEV_USER_KEY, email)
          window.location.reload()
        }
      }
    },
    signInWithPassword: async (email: string, password: string) => {
      try {
        await api.post('/auth/password', { email, password })
        // Read the principal rather than reloading. The sign-in screen renders
        // in place of the routes, so the URL was never left: someone who
        // followed a link to one decision, was asked to sign in, and did, is
        // already on that decision's path and the app appears around them.
        //
        // The reload this replaces also worked, and for the same reason, but
        // it threw away the loaded application to learn one fact the service
        // will answer in a single request.
        setPrincipal(await api.get<Principal>('/auth/me'))
        return null
      } catch (e) {
        // One message for every way it fails, matching what the service
        // answers: which of them is true is what someone working through a
        // list is asking.
        const status = e instanceof ApiError ? e.problem.status : 0
        if (status === 404) return 'Password sign-in is not enabled on this deployment.'
        if (status === 429) return 'Too many attempts. Wait a minute and try again.'
        if (status === 403) return 'That account is not active.'
        return 'That email and password do not match an account.'
      }
    },
    signOut: () => {
      // Three stores hold a way back in, and clearing two of them is what made
      // signing out look like it worked.
      //
      // The cookie is cleared by /auth/logout and the development name by the
      // line below. MSAL keeps its own account and tokens in sessionStorage,
      // which survives a reload and is emptied only when the tab closes. So the
      // reload ran the silent sign-in at boot, it found the cached account,
      // renewed a token and posted it back, and the session was rebuilt before
      // the page finished loading. Every part worked; nobody owned the whole.
      //
      // clearCache and not logoutRedirect: this signs the person out of this
      // console, not out of Microsoft, and taking them out of Outlook to leave
      // a settings page would be its own defect.
      localStorage.removeItem(DEV_USER_KEY)
      const done = () => window.location.reload()
      void forgetMsalSession(sso)
        .catch((err) => {
          // Never a reason to stay signed in. If the cache cannot be cleared
          // the server session still ends, and the boot then finds an account
          // whose token no longer buys anything.
          console.warn('could not clear the sign-in cache:', err)
        })
        .then(() => api.post('/auth/logout', {}))
        .finally(done)
    },
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

/**
 * What to show a person about a refusal.
 *
 * The title is chosen from the status code alone, so it is the same sentence
 * for every 403 whatever actually happened. The detail is written at the raise
 * site and is the only part that knows. Shown when it adds something, which is
 * when it differs from the title.
 */
export function problemText(error: ApiError): string {
  const { title, detail } = error.problem
  if (detail && detail.toLowerCase() !== title.toLowerCase()) {
    return `${title}: ${detail}`
  }
  return title
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext)
  if (!state) throw new Error('useAuth used outside AuthProvider')
  return state
}

/**
 * MSAL, with the two settings Service Portal paid to discover.
 *
 * navigateToLoginRequestUrl is false because the default bounces through a
 * reload that loses the token before it reaches the backend. The token asked
 * for is this service's own scope, not User.Read: validation is local against
 * our own audience, so no Graph token is involved.
 */
/**
 * The silent attempt at boot, which may not take the page down with it.
 *
 * A failure here is answered by the screen this console already has: the
 * sign-in form. The reason goes to the log, where whoever is diagnosing can
 * read it, rather than onto a page where it would tell the person nothing they
 * can act on.
 */
async function quietSignIn(config: SsoConfig): Promise<void> {
  try {
    await signInWithMsal(config, { silent: true })
  } catch (err) {
    const failure = describeSignInFailure(err)
    if (failure.clearCache) {
      const dropped = clearStuckSignInState([
        window.sessionStorage,
        window.localStorage,
      ])
      // Once, and then the page proceeds to the sign-in screen. Retrying here
      // would hide the fact that it happened at all.
      console.warn(
        `sign-in state was stuck, dropped ${dropped.length} key(s): ${failure.reason}`,
      )
      return
    }
    console.info(`silent sign-in did not establish a session: ${failure.reason}`)
  }
}

/** One place that builds the client, so sign in and sign out cannot disagree
 *  about which cache they are talking to. */
async function msalClient(config: SsoConfig) {
  const { PublicClientApplication } = await import('@azure/msal-browser')
  const msal = new PublicClientApplication({
    auth: {
      clientId: config.client_id,
      authority: `https://login.microsoftonline.com/${config.tenant_id}`,
      redirectUri: `${window.location.origin}${MSAL_REDIRECT_PATH}`,
    },
    cache: { cacheLocation: 'sessionStorage' },
  })
  await msal.initialize()
  return msal
}

/** Forget this console's own session, and only this console's. */
async function forgetMsalSession(config: SsoConfig | null): Promise<void> {
  if (!config?.client_id) return
  const msal = await msalClient(config)
  await msal.clearCache()
}

async function signInWithMsal(config: SsoConfig, opts: { silent: boolean }) {
  const msal = await msalClient(config)

  const scopes = [`api://${config.client_id}/console`]
  // navigateToLoginRequestUrl moved in MSAL v5: it is an option on this call,
  // not a field on the configuration where Service Portal sets it. Same
  // meaning, same reason for false. The default of true bounces through a
  // reload that loses the token before it reaches the backend, which is the
  // discovery Service Portal paid for and we take without repeating.
  const result = await msal.handleRedirectPromise({
    navigateToLoginRequestUrl: false,
  })
  const account = result?.account ?? msal.getAllAccounts()[0]

  if (!account) {
    if (opts.silent) return
    await msal.loginRedirect({ scopes })
    return
  }

  const token = await msal.acquireTokenSilent({ scopes, account })
  await api.post('/auth/ms/callback', { access_token: token.accessToken })
}
